/**
 * Section vocabulary for the public profile / data room.
 *
 * Single source of truth shared by:
 *  - the share-link visibility allowlist (`VisibilityConfig.sections` in
 *    `packages/seedforge/features/src/share/types.ts`, and
 *    `shareLinkSectionKeySchema` in `apps/backend/src/domain/share-link.models.ts`)
 *  - the scroll-dwell tracker, which writes the key a visitor is looking at into
 *    `DataRoomViewingTime.resourceId` while `section` stays `'profile'`.
 *
 * Two distinct sets, deliberately:
 *
 * `SHARE_LINK_SECTION` is what a founder can hide on a link. The evaluation is
 * not among them: it never reaches a link, only the founder's own Feedback page.
 * Stored configs may still carry the retired keys; `sanitizeVisibilityConfig`
 * drops them on read.
 *
 * `PROFILE_VIEW_SECTION` is what the tracker can observe. It is finer, because
 * dwell on the summary is a different signal from dwell on the risks, and it is
 * driven by `data-section` attributes in the rendered profile.
 *
 * Where a concept exists in both, the spelling MUST match (`team`, `pitch`,
 * `risks`, `tensions`, `documents`) — founder-facing labels are resolved from a
 * single map in `ShareLinkRepository.normalizeSectionLabel`, so a second
 * spelling for one concept surfaces as two rows in the same timeline.
 */
export const SHARE_LINK_SECTION = {
    FUNDRAISING: 'fundraising',
    PITCH: 'pitch',
    DOCUMENTS: 'documents',
    TEAM: 'team',
};
export const SHARE_LINK_SECTION_VALUES = ['fundraising', 'pitch', 'documents', 'team'];
/**
 * Scroll-observable sections, in render order. Each value must appear as a
 * `data-section` attribute on exactly one element, and no marked element may
 * contain another — `resolveActiveSection` returns the first section straddling
 * the viewport anchor, which is only well-defined for non-nested elements.
 */
export const PROFILE_VIEW_SECTION = {
    HEADER: 'header',
    DESCRIPTION: 'description',
    TEAM: 'team',
    EVALUATION: 'evaluation',
    SUMMARY: 'summary',
    PITCH: 'pitch',
    DIMENSIONS: 'dimensions',
    RISKS: 'risks',
    TENSIONS: 'tensions',
    DOCUMENTS: 'documents',
    /**
     * The inline pitch-deck preview. Distinct from `documents` (the additional
     * documents list) and from `SHARE_LINK_SECTION.PITCH`/`PROFILE_VIEW_SECTION.PITCH`
     * (the pitch *recording*). While this is the visible section, the tracker
     * routes heartbeats to the `document` page-level path instead of a plain
     * dwell-time row — see `useProfileTracker`.
     */
    DECK: 'deck',
};
export const PROFILE_VIEW_SECTION_VALUES = [
    'header',
    'description',
    'team',
    'evaluation',
    'summary',
    'pitch',
    'dimensions',
    'risks',
    'tensions',
    'documents',
    'deck',
];
/** Founder-facing labels for every section key, both vocabularies. */
export const PROFILE_SECTION_LABELS = {
    header: 'Overview',
    description: 'Description',
    team: 'Team',
    evaluation: 'Evaluation',
    summary: 'Summary',
    pitch: 'Pitch',
    dimensions: 'Dimensions',
    risks: 'Risks',
    tensions: 'Tensions',
    documents: 'Documents',
    deck: 'Deck',
    fundraising: 'Fundraising',
    score: 'Score',
    personas: 'Personas',
};
//# sourceMappingURL=profile-sections.js.map